<template>
  <page-template :title="pageTitle" :description="pageDescription" :image="pageImage">
    <!-- Header Intro Block -->
    <header-intro-block
      :title="pageTitle"
      :preamble="pagePreamble"
      :image="pageImage"
      :mascot="portalMascots"
      :includes="pageIncludes"
      :button-text="pageButtonText"
      :button-url="pageButtonUrl"
    />
    <!-- Upper blocks (CTAs and TextBlocks above articles) -->
    <template v-if="upperBlocks.length > 0">
      <generic-block v-for="block in upperBlocks" :key="block.sys.id" :block-data="block" />
    </template>

    <card-list-block v-if="props.articles?.length" :items="props.articles">
      <template #default="{ item }">
        <content-card
          :article="item"
          :link="(item as any)._path || (item as any).fields?.slug || '#'"
        />
      </template>
    </card-list-block>
    <slot />

    <!-- Lower blocks (CTAs and TextBlocks below articles) -->
    <template v-if="lowerBlocks.length > 0">
      <generic-block v-for="block in lowerBlocks" :key="block.sys.id" :block-data="block" />
    </template>
  </page-template>
</template>

<script setup lang="ts">
import type { Article as ContentfulArticle } from '@/types/contentful'

// Define a union type for page data
type ContentfulPageData = {
  title: string
  preamble: string
  metadescription: string
  image?: string | null
  mascot?: string[]
  tags: {
    links: any[]
    moreLink: any
  }
  upperBlocks?: any[]
  lowerBlocks?: any[]
  urlText?: string
  url?: string
}

type PageData = ContentfulPageData

const props = defineProps<{
  page: PageData
  theme: 'pink' | 'green' | 'purple' | 'blue' | 'beige' | 'grey'
  articles?: ContentfulArticle[]
}>()

// Get page title from either structure
const pageTitle = computed(() => {
  if ('title' in props.page) {
    return props.page.title
  }
  return ''
})

// Get page preamble from either structure
const pagePreamble = computed(() => {
  if ('preamble' in props.page) {
    return props.page.preamble
  }
  return ''
})

// Get page description from either structure
const pageDescription = computed(() => {
  if ('metadescription' in props.page && props.page.metadescription) {
    return props.page.metadescription
  }
  if ('preamble' in props.page) {
    return props.page.preamble
  }
  return ''
})

// Get portal mascots from page data
const portalMascots = computed(() => {
  if ('mascot' in props.page && props.page.mascot) {
    return props.page.mascot
  }
  return []
})

// Get page image from page data
const pageImage = computed(() => {
  if ('image' in props.page && props.page.image) {
    return props.page.image
  }
  return null
})

// Get page includes from page data
const pageIncludes = computed(() => {
  if ('includes' in props.page && props.page.includes) {
    return props.page.includes
  }
  return null
})

// Get page button text from page data
const pageButtonText = computed(() => {
  if ('urlText' in props.page && props.page.urlText) {
    return props.page.urlText
  }
  return ''
})

// Get page button URL from page data
const pageButtonUrl = computed(() => {
  if ('url' in props.page && props.page.url) {
    return props.page.url
  }
  return ''
})

// Get upper blocks (CTAs above articles)
const upperBlocks = computed(() => {
  if ('upperBlocks' in props.page && props.page.upperBlocks) {
    return props.page.upperBlocks
  }
  return []
})

// Get lower blocks (CTAs below articles)
const lowerBlocks = computed(() => {
  if ('lowerBlocks' in props.page && props.page.lowerBlocks) {
    return props.page.lowerBlocks
  }
  return []
})
</script>
